"use client";
/* eslint-disable @next/next/no-img-element */
import { forwardRef, memo, useEffect, useMemo, useState } from "react";
import { getAssetsUrl } from "utils";

const DEFAULT_PLACEHOLDER_SRC = "/assets/images/image_place_holder.png";

/**
 * 阿里云 OSS 支持的图片格式
 * 参考：https://help.aliyun.com/document_detail/44688.html
 */
const SUPPORTED_IMAGE_FORMATS = [
  "jpg",
  "jpeg",
  "png",
  "webp",
  "bmp",
  "gif",
  "tiff",
  "tif",
  "jp2", // JPEG 2000
  "jpx", // JPEG 2000
] as const;

/**
 * 响应式图片宽度断点
 * 参考业界最佳实践（Cloudinary, Imgix, Next.js Image）
 *
 * 设计理念：
 * - 小尺寸 (100-400): 移动端缩略图、图标、头像
 * - 中尺寸 (400-800): 移动端主图、平板、PC 小图
 * - 大尺寸 (800-2000): PC 主图、高清大图、Banner
 *
 * 倍数关系：每个断点约为前一个的 1.5-2 倍，平衡性能和质量
 */
const IMAGE_WIDTH_BREAKPOINTS = [
  100, // 移动端小图标、头像
  200, // 移动端缩略图
  400, // 移动端列表图、小卡片
  600, // 移动端主图、平板小图
  800, // 平板主图、PC 小图
  1000, // PC 中图
  1200, // PC 大图
  1600, // 高清大图
  2000, // 超高清、Banner
] as const;

/**
 * 获取文件扩展名
 */
const getFileExtension = (url: string): string => {
  try {
    // 移除查询参数和 hash
    const urlWithoutParams = url.split("?")[0].split("#")[0];
    // 获取最后一个点后面的内容
    const ext = urlWithoutParams.split(".").pop()?.toLowerCase() || "";
    return ext;
  } catch {
    return "";
  }
};

/**
 * 检查是否为支持的图片格式
 */
const isSupportedImageFormat = (url: string): boolean => {
  const ext = getFileExtension(url);
  return SUPPORTED_IMAGE_FORMATS.includes(ext as any);
};

/**
 * 向上匹配最接近的宽度断点
 *
 * @param targetWidth 目标宽度
 * @returns 匹配的断点宽度
 *
 * @example
 * getClosestBreakpoint(150) // => 200
 * getClosestBreakpoint(500) // => 600
 * getClosestBreakpoint(800) // => 800
 * getClosestBreakpoint(2500) // => 2000 (最大值)
 */
const getClosestBreakpoint = (targetWidth: number): number => {
  // 如果目标宽度小于等于最小断点，返回最小断点
  if (targetWidth <= IMAGE_WIDTH_BREAKPOINTS[0]) {
    return IMAGE_WIDTH_BREAKPOINTS[0];
  }

  // 如果目标宽度大于等于最大断点，返回最大断点
  const maxBreakpoint =
    IMAGE_WIDTH_BREAKPOINTS[IMAGE_WIDTH_BREAKPOINTS.length - 1];
  if (targetWidth >= maxBreakpoint) {
    return maxBreakpoint;
  }

  // 向上查找最接近的断点
  for (const breakpoint of IMAGE_WIDTH_BREAKPOINTS) {
    if (breakpoint >= targetWidth) {
      return breakpoint;
    }
  }

  // 兜底返回最大断点
  return maxBreakpoint;
};

/**
 * 为阿里云 OSS 图片添加压缩参数
 *
 * 限制：
 * 1. 仅支持特定图片格式（JPEG、PNG、WebP、BMP、GIF、TIFF、JPEG 2000）
 * 2. 宽度会匹配预定义的断点数组，向上取值，最大 2000
 * 3. 仅处理 HTTP/HTTPS 协议的 URL
 *
 * @param src 原始图片地址
 * @param width 目标图片宽度
 * @returns 处理后的图片地址
 *
 * @example
 * addOssImageProcess('https://example.com/image.jpg', 750)
 * // => 'https://example.com/image.jpg?image_process=resize,w_800'
 *
 * addOssImageProcess('https://example.com/image.jpg?foo=bar', 1500)
 * // => 'https://example.com/image.jpg?foo=bar&image_process=resize,w_1600'
 *
 * addOssImageProcess('https://example.com/image.svg', 800)
 * // => 'https://example.com/image.svg' (不支持的格式)
 *
 * addOssImageProcess('/assets/image.jpg', 800)
 * // => '/assets/image.jpg' (本地路径不处理)
 */
const addOssImageProcess = (src: string, width?: number): string => {
  // 如果 src 为空或 width 不是数字，直接返回原 src
  if (!src || typeof width !== "number" || width <= 0) {
    return src;
  }

  // 只处理 http 或 https 协议的 URL
  if (!src.startsWith("http://") && !src.startsWith("https://")) {
    return src;
  }

  // 检查是否为支持的图片格式
  if (!isSupportedImageFormat(src)) {
    return src;
  }

  // 检查是否已经有 image_process 参数，避免重复拼接
  if (src.includes("image_process=")) {
    return src;
  }

  // 匹配最接近的宽度断点（向上取值）
  const matchedWidth = getClosestBreakpoint(width);

  // 判断是否已经有查询参数，决定使用 ? 还是 &
  const separator = src.includes("?") ? "&" : "?";

  // 拼接压缩参数
  return `${src}${separator}image_process=resize,w_${matchedWidth}`;
};

// 将 defaultSrc 的计算移到组件外部
const getDefaultSrc = (path: string | undefined) => {
  if (!path) {
    return "";
  }
  const result = getAssetsUrl(path);
  return result;
};
const DEFAULT_SRC = getDefaultSrc(DEFAULT_PLACEHOLDER_SRC);

/**
 * 网络/本地图片通用 props。
 * 注：这里使用原生 img 标签而不是 next/image，因为：
 * 1. 处理的是不确定域名的外部图片
 * 2. next/image 需要在 next.config.js 中配置允许的图片域名
 * 3. 外部图片域名不固定，无法预先配置
 */
export interface NextImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string | undefined;
  alt?: string;
  width?: number;
  height?: number;
  className?: string;
  style?: React.CSSProperties;
  /**
   * 占位图。传入空字符串 / undefined 表示不使用默认占位图（无加载背景、错误不回退到占位图）。
   * `NextImage` 默认使用 {@link DEFAULT_PLACEHOLDER_SRC}；`AppImage` 默认不使用。
   */
  placeholderSrc?: string;
}

type ImageCoreProps = NextImageProps & {
  /** 未传 placeholderSrc 时的默认值；NextImage 为 DEFAULT，AppImage 为 "" */
  defaultPlaceholderSrc: string;
};

/**
 * 共享图片内核：getAssetsUrl + OSS 压缩 + 可选 placeholder。
 */
const ImageCore = memo(
  forwardRef<HTMLImageElement, ImageCoreProps>(function ImageCore(
    {
      src,
      alt,
      placeholderSrc,
      defaultPlaceholderSrc,
      className = "",
      style,
      loading = "lazy",
      onError,
      onLoad,
      ...props
    },
    ref,
  ) {
    const resolvedPlaceholderSrc =
      placeholderSrc !== undefined ? placeholderSrc : defaultPlaceholderSrc;

    const defaultSrc = useMemo(
      () =>
        resolvedPlaceholderSrc === DEFAULT_PLACEHOLDER_SRC
          ? DEFAULT_SRC
          : getDefaultSrc(resolvedPlaceholderSrc),
      [resolvedPlaceholderSrc],
    );

    const hasPlaceholder = Boolean(defaultSrc);

    // 如果 src 未定义，有 placeholder 则回退，否则空字符串
    // 应用阿里云 OSS 图片压缩参数
    const initialSrc = useMemo(() => {
      if (!src) {
        return defaultSrc;
      }
      const processedSrc = getDefaultSrc(src);
      // 自动添加 OSS 压缩参数
      return addOssImageProcess(processedSrc, props.width);
    }, [src, defaultSrc, props.width]);

    const [imgSrc, setImgSrc] = useState<string>(initialSrc);
    const [isLoaded, setIsLoaded] = useState(false);

    useEffect(() => {
      setImgSrc(initialSrc);
      setIsLoaded(false);
    }, [initialSrc, src]);

    const handleError: React.ReactEventHandler<HTMLImageElement> = (event) => {
      if (hasPlaceholder) {
        setImgSrc(defaultSrc);
      }
      setIsLoaded(false);
      onError?.(event);
    };

    const handleLoad: React.ReactEventHandler<HTMLImageElement> = (event) => {
      setIsLoaded(true);
      onLoad?.(event);
    };

    if (!imgSrc && !hasPlaceholder) {
      return null;
    }

    return (
      <img
        {...props}
        ref={ref}
        src={imgSrc || undefined}
        alt={alt || ""}
        className={className}
        style={{
          ...(isLoaded || !hasPlaceholder
            ? {}
            : {
                backgroundImage: `url(${defaultSrc})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }),
          ...style,
        }}
        onError={handleError}
        onLoad={handleLoad}
        loading={loading}
      />
    );
  }),
);

ImageCore.displayName = "ImageCore";

/**
 * 网络图片组件（带默认占位图）。
 * 加载中显示 DEFAULT_PLACEHOLDER 背景；失败回退到占位图。
 */
export const NextImage = memo(
  forwardRef<HTMLImageElement, NextImageProps>(function NextImage(props, ref) {
    return (
      <ImageCore
        {...props}
        ref={ref}
        defaultPlaceholderSrc={DEFAULT_PLACEHOLDER_SRC}
      />
    );
  }),
);

NextImage.displayName = "NextImage";

/**
 * 通用图片组件（无默认占位图）。
 * 适用于本地静态资源 / 明确已知 URL，避免加载前闪 DEFAULT_PLACEHOLDER。
 * 仍支持 getAssetsUrl、OSS 压缩；仅当显式传入 placeholderSrc 才启用占位逻辑。
 */
export const AppImage = memo(
  forwardRef<HTMLImageElement, NextImageProps>(function AppImage(props, ref) {
    return <ImageCore {...props} ref={ref} defaultPlaceholderSrc="" />;
  }),
);

AppImage.displayName = "AppImage";

export default NextImage;
